import React, { useCallback, useEffect, useState } from 'react';
import { actions } from '../../context';
import { Col, Container, Row } from 'reactstrap';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';

import OverviewActivity from './OverviewActivities';
import OverviewStatus from './PracticeOverview/Practice/PracticeStatus';
import PracticeOverview from './PracticeOverview/PracticeOverview';
import Loader from './PracticeOverview/Loader';
import Communications from './Communications/Communications';
import hasPermission from '../Shared/hasPermission';
import PracticeAlerts from './PracticeOverview/PracticeAlerts/PracticeAlerts';

const Overview = () => {
    const dispatch = useDispatch();

    const { practiceId } = useParams();
    const [isPracticeDataLoading, setIsPracticeDataLoading] = useState(true);

    const {
        isEditing,
        isMutePracticeModalOpen,
        triggerPracticeRefresh,
        isPracticeScheduleModalOpen,
        daylightCodeModalVisible,
    } = useSelector(
        (state) => ({
            isEditing: state.toggle.isEditingPractice,
            // TODO: refactor the data sync's and rip all this out to go in there
            isMutePracticeModalOpen: state.toggle.isMutePracticeModalOpen,
            triggerPracticeRefresh: state.toggle.triggerPracticeRefresh,
            isPracticeScheduleModalOpen:
                state.toggle.isPracticeScheduleModalOpen,
            daylightCodeModalVisible: state.daylight.daylightCodeModalVisible,
        }),
        shallowEqual
    );

    const isLoading = useSelector(state => state.practice.isPracticeLoading);

    // TODO: put permissions at the app level since they probably wont change and pull the primitives out at the component level
    // will prevent so many evaluations of shalloweEqual, make it a Set instead of array?
    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );
    const canSeeCommunications = hasPermission(`Communication`, permissions);
    const canSeeActivity = hasPermission(`Activity`, permissions);
    const canViewAllPractices = hasPermission('ViewAllPractices', permissions);

    let intervalFunction;
    // TODO: console error: "axios error:  {}"
    const refresh = useCallback(
        (type) => {
            if (type === 'onload') {
                setIsPracticeDataLoading(true);
                dispatch(actions.practice.fetchPractice(practiceId)).then(() =>
                    setIsPracticeDataLoading(false)
                );
            } else {
                dispatch(actions.practice.fetchPractice(practiceId));
            }
        },
        [dispatch, practiceId]
    );

    // TODO: There is a small delay between practices list update and practice page update for things like bookmark icon
    // useLayoutEffect force synchronous behavior combined with optimistic update?

    // immediate call for practice information
    useEffect(() => {
        dispatch(actions.practice.getDashboardStats());
        refresh('onload');
    }, [
        dispatch,
        refresh,
        // TODO: figure out a better way to do this forced refresh,
        // checking to see if the currently mounted practice is the subject of the action so we can conditionally force a refresh
        triggerPracticeRefresh,
    ]);

    // delayed practice info call, recurring every 30 seconds
    useEffect(() => {
        clearInterval(intervalFunction);
        // check conditions which should stop interval loop
        if (
            isEditing ||
            isMutePracticeModalOpen ||
            isPracticeScheduleModalOpen ||
            daylightCodeModalVisible
        ) {
            return;
        }
        // eslint-disable-next-line
        intervalFunction = setInterval(
            async () => refresh('dataSync'),
            1000 * 30
        );
        return () => clearInterval(intervalFunction);
    }, [
        // dependent closure values
        dispatch,
        isEditing,
        isMutePracticeModalOpen,
        isPracticeScheduleModalOpen,
        daylightCodeModalVisible,
        // this will force clear the interval function when we switch practices, refresh was using stale practice id closure
        practiceId,
        // not dependent for closure but force a render when changed
        triggerPracticeRefresh,
    ]);

    let content = <>
        {/* Section that has both the Practice Details section and Practice Configuration section */}
        <PracticeOverview />

        {/* Section for the Practice Alerts */}
        {isPracticeDataLoading ? (
            <Loader value={'Practice Alerts'} />
        ) : (
            canViewAllPractices && <PracticeAlerts practiceId={practiceId} triggerRefresh={triggerPracticeRefresh} />
        )}

        {/* Section for the Sync cards and Conversion rows */}
        <OverviewStatus />

        {canSeeCommunications && <Communications />}

        {/* Section that has the Activities feed for the practice ("Notes" entity) */}
        {canSeeActivity && <OverviewActivity />}
    </>

    if (isLoading === true)
    {
        content = <>
            <Col className="p-3 text-center sc-glass sc-rounded sc-shadow text-light">
                <Loader />
            </Col>
        </>;
    }

    return (
        <Container className="m-0 p-0">
            <Row className="m-0 d-flex flex-wrap">
                {isLoading || isPracticeDataLoading ? ( //TODO: refactor this to use a single isLoading state
                    <Col className="p-3 text-center sc-glass sc-rounded sc-shadow text-light">
                        <Loader value={'Practice'} />
                    </Col>
                ) : (
                   content
                )}
            </Row>
        </Container>
    );
};

export default React.memo(Overview);
